import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SiteSettings {
  desktop_logo_url: string | null;
  desktop_logo_height: number;
  mobile_logo_url: string | null;
  mobile_logo_height: number;
  dark_desktop_logo_url: string | null;
  dark_mobile_logo_url: string | null;
  footer_logo_url: string | null;
  footer_dark_logo_url: string | null;
  footer_desktop_height: number;
  footer_mobile_height: number;
  logo_x_offset: number;
  logo_y_offset: number;
  footer_x_offset: number;
  footer_y_offset: number;
  favicon_url: string | null;
  venue_dup_name_similarity: number;
  venue_dup_distance_meters: number;
  artist_dup_name_similarity: number;
  venue_request_sla_hours: number;
  payout_weekday: number;
  hero_enabled: boolean;
  hero_title_line1: string | null;
  hero_title_line2: string | null;
  hero_subtitle: string | null;
  hero_cta_label: string | null;
  hero_search_placeholder: string | null;
  show_event_statistics: boolean;
}

const DEFAULT_SETTINGS: SiteSettings = {
  desktop_logo_url: null,
  desktop_logo_height: 36,
  mobile_logo_url: null,
  mobile_logo_height: 32,
  dark_desktop_logo_url: null,
  dark_mobile_logo_url: null,
  footer_logo_url: null,
  footer_dark_logo_url: null,
  footer_desktop_height: 36,
  footer_mobile_height: 28,
  logo_x_offset: 0,
  logo_y_offset: 0,
  footer_x_offset: 0,
  footer_y_offset: 0,
  favicon_url: null,
  venue_dup_name_similarity: 0.85,
  venue_dup_distance_meters: 75,
  artist_dup_name_similarity: 0.85,
  venue_request_sla_hours: 48,
  payout_weekday: 3,
  hero_enabled: true,
  hero_title_line1: null,
  hero_title_line2: null,
  hero_subtitle: null,
  hero_cta_label: null,
  hero_search_placeholder: null,
  show_event_statistics: true,
};

let cachedSettings: SiteSettings | null = null;

try {
  const local = localStorage.getItem("biletime_settings_cache");
  if (local) {
    cachedSettings = JSON.parse(local);
  }
} catch {}

let listeners: Array<(s: SiteSettings) => void> = [];
let pendingSettingsRequest: Promise<void> | null = null;

const notify = (s: SiteSettings) => {
  cachedSettings = s;
  try {
    localStorage.setItem("biletime_settings_cache", JSON.stringify(s));
  } catch {}
  listeners.forEach(fn => fn(s));
};

const fetchSiteSettings = async () => {
  if (cachedSettings || pendingSettingsRequest) return pendingSettingsRequest;

  pendingSettingsRequest = (async () => {
    try {
      const { data } = await supabase
        .from("site_settings")
        .select("*")
        .eq("id", "default")
        .maybeSingle();

      if (!data) return;

      const d = data;
      notify({
        desktop_logo_url: d.desktop_logo_url,
        desktop_logo_height: d.desktop_logo_height ?? 36,
        mobile_logo_url: d.mobile_logo_url,
        mobile_logo_height: d.mobile_logo_height ?? 32,
        dark_desktop_logo_url: d.dark_desktop_logo_url,
        dark_mobile_logo_url: d.dark_mobile_logo_url,
        footer_logo_url: d.footer_logo_url,
        footer_dark_logo_url: d.footer_dark_logo_url,
        footer_desktop_height: d.footer_desktop_height ?? 36,
        footer_mobile_height: d.footer_mobile_height ?? 28,
        logo_x_offset: d.logo_x_offset ?? 0,
        logo_y_offset: d.logo_y_offset ?? 0,
        footer_x_offset: d.footer_x_offset ?? 0,
        footer_y_offset: d.footer_y_offset ?? 0,
        favicon_url: d.favicon_url,
        venue_dup_name_similarity: typeof d.venue_dup_name_similarity === "number" ? d.venue_dup_name_similarity : 0.85,
        venue_dup_distance_meters: typeof d.venue_dup_distance_meters === "number" ? d.venue_dup_distance_meters : 75,
        artist_dup_name_similarity: typeof d.artist_dup_name_similarity === "number" ? d.artist_dup_name_similarity : 0.85,
        venue_request_sla_hours: typeof d.venue_request_sla_hours === "number" ? d.venue_request_sla_hours : 48,
        payout_weekday: typeof d.payout_weekday === "number" ? d.payout_weekday : 3,
        hero_enabled: typeof d.hero_enabled === "boolean" ? d.hero_enabled : true,
        hero_title_line1: d.hero_title_line1 ?? null,
        hero_title_line2: d.hero_title_line2 ?? null,
        hero_subtitle: d.hero_subtitle ?? null,
        hero_cta_label: d.hero_cta_label ?? null,
        hero_search_placeholder: d.hero_search_placeholder ?? null,
        show_event_statistics: typeof d.show_event_statistics === "boolean" ? d.show_event_statistics : true,
      });
    } finally {
      pendingSettingsRequest = null;
    }
  })();

  return pendingSettingsRequest;
};

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>(cachedSettings || DEFAULT_SETTINGS);

  useEffect(() => {
    listeners.push(setSettings);

    if (!cachedSettings) {
      void fetchSiteSettings();
    }

    return () => {
      listeners = listeners.filter(fn => fn !== setSettings);
    };
  }, []);

  return settings;
}

export async function updateSiteSettings(partial: Partial<SiteSettings>) {
  const { error } = await supabase
    .from("site_settings")
    .update({ ...partial, updated_at: new Date().toISOString() })
    .eq("id", "default");

  if (!error) {
    const merged = { ...(cachedSettings || DEFAULT_SETTINGS), ...partial };
    notify(merged);
  }
  return { error };
}
